import { useCallback, useEffect } from 'react';
import { Modal, Form, Input, Spin } from 'antd-v5';
import BlackButton from '../../components/BlackButton';
import PictureUpload from '../../components/PictureUpload';
import { uploadFileToOss } from '../../utils/ossUpload';
import styles from './index.module.scss';
import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';

interface VerifySnModalProps {
    open: boolean;
    locale: string;
    onCancel?: () => void;
    onSuccess?: (sn: string, snUrl: string, deviceUrl: string) => void;
    loading?: boolean;
    error?: string | null;
}

interface FormValues {
    sn: string;
    snUrl: string | null;
    deviceUrl: string | null;
}

const VerifySnModal: React.FC<VerifySnModalProps> = ({ open, locale, onCancel, onSuccess, loading, error }) => {
    const { t } = useLang('VerifySnModal');
    const [form] = Form.useForm<FormValues>();

    useEffect(() => {
        if (!open) {
            form.resetFields();
        }
    }, [open, form]);

    const uploadSingleFile = useCallback((file: File) => uploadFileToOss(file, locale), [locale]);

    const handleFinish = (values: FormValues) => {
        if (!values.snUrl || !values.deviceUrl) return;
        onSuccess?.(values.sn.trim(), values.snUrl, values.deviceUrl);

        gtm.push({
            event: 'uaEvent',
            event_name: 'pre_order_submit',
            form_type: 'Service Renewal',
            form_info: 'Verify SN',
            button_name: t('confirm'),
            status: 'success',
        });
    };

    return (
        <Modal className={styles.VerifySnModal} open={open} onCancel={onCancel} footer={null} centered maskClosable={false} title="Verify SN" destroyOnClose>
            <Spin spinning={loading}>
                <Form form={form} onFinish={handleFinish} layout="vertical" requiredMark={false}>
                    <Form.Item name="sn" label={t('sn_label')} rules={[{ required: true, message: t('error_sn') }]}>
                        <Input placeholder="SN" />
                    </Form.Item>

                    <div className={styles.uploadRow}>
                        <Form.Item name="snUrl" label={t('sn_photo')} rules={[{ required: true, message: t('error_sn_photo') }]}>
                            <PictureUpload uploadFn={uploadSingleFile} />
                        </Form.Item>

                        <Form.Item name="deviceUrl" label={t('full_device_photo')} rules={[{ required: true, message: t('error_full_device_photo') }]}>
                            <PictureUpload uploadFn={uploadSingleFile} />
                        </Form.Item>
                    </div>

                    {error && <p className={styles.modalError}>*{error}</p>}

                    <div className={styles.modalFooter}>
                        <BlackButton type="primary" shape="round" htmlType="submit" block disabled={loading}>
                            {t('confirm')}
                        </BlackButton>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
};

export default VerifySnModal;
